import type { EstimatorInput, EstimatorResult } from "@reno-estimator/core";
import type { LeadContactInfo, LeadCta, LeadPhotoDescriptor, LeadRecord } from "./types.js";
import { classifyLead, DEFAULT_QUALIFICATION_CONFIG, type QualificationConfig } from "./qualification.js";
import { buildLeadFingerprint } from "./duplicate-protection.js";
import { ESTIMATOR_VERSION } from "./version.js";

export interface BuildLeadRecordParams {
  cta: LeadCta;
  input: EstimatorInput;
  result: EstimatorResult;
  contact: LeadContactInfo;
  photos?: LeadPhotoDescriptor[];
  source?: string;
  qualificationConfig?: QualificationConfig;
  estimatorVersion?: string;
  now?: () => Date;
  idFactory?: () => string;
}

function defaultId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `lead-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Assembles the full LeadRecord the brief calls for - customer info,
 * project context, the estimate, and version stamps - plus runs
 * qualification and computes the dedupe fingerprint. Pure/synchronous:
 * storage and duplicate/spam checks are the caller's job (see
 * submit-lead.ts, which orchestrates all of it for the UI).
 */
export function buildLeadRecord(params: BuildLeadRecordParams): LeadRecord {
  const now = (params.now ?? (() => new Date()))();
  const id = (params.idFactory ?? defaultId)();
  const qualification = classifyLead(
    { input: params.input, result: params.result, contact: params.contact },
    params.qualificationConfig ?? DEFAULT_QUALIFICATION_CONFIG
  );

  return {
    id,
    createdAt: now.toISOString(),
    cta: params.cta,
    contact: params.contact,
    photos: params.photos ?? [],
    projectType: params.input.projectType,
    location: params.input.location,
    approximateSizeSqft: params.input.roomSize?.unit === "sqft" ? params.input.roomSize.value : undefined,
    scope: params.input.scope,
    finishLevel: params.input.finishLevel,
    estimatedBudget: params.result.estimate,
    timeline: params.input.timeline,
    notes: params.contact.projectDescription,
    estimatorVersion: params.estimatorVersion ?? ESTIMATOR_VERSION,
    pricingVersion: params.result.pricingVersion,
    qualification,
    dedupeFingerprint: buildLeadFingerprint(params.contact, params.input.projectType),
    source: params.source
  };
}
